import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { IPaginatedResponse } from "../models/Pagination";
import {
  ICreateProductionProject,
  ICreateProductionProjectDocuments,
  ICreateProductionProjectMember,
  ICreateProductionProjectPhase,
  ICreateProductionProjectPhaseMilestone,
  ICreateProductionPurchaseRequest,
  IProductionListItemAwb,
  IProductionProject,
  IProductionProjectDocumentDownloadHistory,
  IProductionProjectDocuments,
  IProductionProjectMember,
  IProductionProjectOverview,
  IProductionProjectPhaseMilestone,
  IProductionProjectPhases,
  IProductionPurchaseRequest,
  IUpdateProductionProject,
  IUpdateProductionProjectMember,
  IUpdateProductionProjectPhase,
  IUpdateProductionPurchaseRequest,
} from "../models/Production";
import { IFilterResearch } from "../models/Research";
import { productionService } from "../services/productionService";

export function useGetProductionProject(
  query: IFilterResearch,
): UseQueryResult<IPaginatedResponse<IProductionProject>> {
  return useQuery({
    queryKey: ["production-projects", query],
    queryFn: () => productionService.getAllProductionProject(query),
  });
}
export function useCreateProductionProject(): UseMutationResult<
  IProductionProject,
  unknown,
  ICreateProductionProject,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateProductionProject) =>
      productionService.createProductionProject(data),
    onSuccess: (data) => {
      queryClient.setQueriesData(
        {
          queryKey: ["production-projects"],
        },
        (oldData: IPaginatedResponse<IProductionProject>) => {
          if (oldData) {
            return {
              ...oldData,
              data: [data, ...oldData.data],
            };
          }
          return oldData;
        },
      );
    },
  });
}
export function useImportListRequest() {
  return useMutation({
    mutationFn: productionService.importListRequest,
  });
}
export function useUpdateProductionProject(): UseMutationResult<
  IProductionProject,
  unknown,
  { id: number; data: IUpdateProductionProject },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: number; data: IUpdateProductionProject }) =>
      productionService.updateProductionProject(body.id, body.data),
    onSuccess: (data) => {
      queryClient.setQueriesData(
        {
          queryKey: ["production-project", data.alias],
        },
        (oldData: IProductionProject) => {
          if (oldData) {
            return {
              ...oldData,
              ...data,
            };
          }
          return oldData;
        },
      );
    },
  });
}
export function useGetProductionProjectByAlias(
  alias: string,
): UseQueryResult<IProductionProject> {
  return useQuery({
    queryKey: ["production-project", alias],
    queryFn: () => productionService.getProductionProjectByAlias(alias),
    enabled: !!alias,
  });
}

export function useCreateProductionProjectMember(): UseMutationResult<
  IProductionProjectMember,
  unknown,
  ICreateProductionProjectMember,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateProductionProjectMember) =>
      productionService.createProductionProjectMember(data),
    onSuccess(data) {
      queryClient.setQueriesData(
        {
          queryKey: ["prd-project-member", data.prdProjectId],
        },
        (oldData: IProductionProjectMember[]) => {
          if (oldData) {
            return [...oldData, data];
          }
          return data;
        },
      );
    },
  });
}
export function useGetProductionProjectMember(
  id: number,
): UseQueryResult<IProductionProjectMember[]> {
  return useQuery({
    queryKey: ["prd-project-member", id],
    queryFn: () => productionService.getProductionProjectMember(id),
    enabled: !!id,
  });
}
export function useUpdateProductionProjectMember(): UseMutationResult<
  IProductionProjectMember,
  unknown,
  { id: number; data: IUpdateProductionProjectMember },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: number; data: IUpdateProductionProjectMember }) =>
      productionService.updateProductionProjectMember(body.id, body.data),
    onSuccess: (updatedMember) => {
      queryClient.setQueryData<IProductionProjectMember[]>(
        ["prd-project-member", updatedMember.prdProjectId],
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((item) =>
            item.id === updatedMember.id ? updatedMember : item,
          );
        },
      );
    },
  });
}
export function useDeleteProductionProjectMember(): UseMutationResult<
  number,
  unknown,
  number,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      productionService.deleteProductionProjectMember(id),
    onSuccess: (data, variables) => {
      queryClient.setQueryData<IProductionProjectMember[]>(
        ["prd-project-member", data.prdProjectId],
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.filter((item) => item.id !== variables);
        },
      );
    },
  });
}
export function useGetProductionPurchaseRequest(
  prdProjectId: number,
  partNumber?: string,
): UseQueryResult<IProductionPurchaseRequest[]> {
  return useQuery({
    queryKey: ["production-purchase-request", prdProjectId, partNumber],
    queryFn: () =>
      productionService.getProductionPurchaseRequest(prdProjectId, partNumber),
    enabled: !!prdProjectId,
  });
}
export function useCreateProductionPurchaseRequest(): UseMutationResult<
  IProductionPurchaseRequest,
  unknown,
  ICreateProductionPurchaseRequest,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateProductionPurchaseRequest) =>
      productionService.createProductionPurchaseRequest(data),
    onSuccess(data) {
      queryClient.setQueriesData(
        {
          queryKey: ["production-purchase-request", data.prdProjectId],
        },
        (oldData: IProductionPurchaseRequest[]) => {
          if (oldData) {
            return [data, ...oldData];
          }
          return [data];
        },
      );
    },
  });
}
export function useUpdateProductionPurchaseRequest(): UseMutationResult<
  IProductionPurchaseRequest,
  unknown,
  { id: number; data: IUpdateProductionPurchaseRequest },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      id: number;
      data: IUpdateProductionPurchaseRequest;
    }) => productionService.updateProductionPurchaseRequest(body.id, body.data),
    onSuccess(data) {
      queryClient.setQueriesData(
        {
          queryKey: ["production-purchase-request", data.prdProjectId],
        },
        (oldData: IProductionPurchaseRequest[]) => {
          if (oldData) {
            return oldData.map((item) => (item.id === data.id ? data : item));
          }
          return oldData;
        },
      );
    },
  });
}
export function useRequestProductionPurchaseRequestToDirector(): UseMutationResult<
  IProductionPurchaseRequest,
  unknown,
  number,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      productionService.requestProductionPurchaseRequestToDirector(id),
    onSuccess(data) {
      queryClient.setQueriesData(
        {
          queryKey: ["production-purchase-request", data.prdProjectId],
        },
        (oldData: IProductionPurchaseRequest[]) => {
          if (oldData) {
            return oldData.map((item) => (item.id === data.id ? data : item));
          }
          return oldData;
        },
      );
    },
  });
}
export function useApproveProductionPurchaseRequestByDirector(): UseMutationResult<
  IProductionPurchaseRequest,
  unknown,
  number,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      productionService.approveProductionPurchaseRequestByDirector(id),
    onSuccess(data) {
      queryClient.setQueriesData(
        {
          queryKey: ["production-purchase-request", data.prdProjectId],
        },
        (oldData: IProductionPurchaseRequest[]) => {
          if (oldData) {
            return oldData.map((item) => (item.id === data.id ? data : item));
          }
          return oldData;
        },
      );
    },
  });
}
export function useGetProductionProjectOverview(): UseQueryResult<IProductionProjectOverview> {
  return useQuery({
    queryKey: ["production-project-overview"],
    queryFn: () => productionService.getProductionProjectOverview(),
  });
}
export function useGetProductionListItemAwbs(
  id: number,
): UseQueryResult<IProductionListItemAwb[]> {
  return useQuery({
    queryKey: ["production-list-item-awbs"],
    queryFn: () => productionService.getProductionListItemAwbs(id),
  });
}
export function useGetProductionProjectPhases(
  projectId: number | undefined,
): UseQueryResult<IProductionProjectPhases[]> {
  return useQuery({
    queryKey: ["prd-project-phase", projectId],
    queryFn: () => productionService.getProductionProjectPhases(projectId!),
    enabled: !!projectId,
  });
}
export function useUpdateProductionProjectPhases(): UseMutationResult<
  IProductionProjectPhases,
  unknown,
  { id: number; data: IUpdateProductionProjectPhase },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: number; data: IUpdateProductionProjectPhase }) =>
      productionService.updateProductionProjectPhases(body.id, body.data),
    onSuccess: (data) => {
      queryClient.setQueriesData(
        { queryKey: ["prd-project-phase", data.prdProjectId] },
        (oldData: IProductionProjectPhases[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((item) => (item.id === data.id ? data : item));
        },
      );
    },
  });
}

export function useCreateProductionProjectPhases(): UseMutationResult<
  IProductionProjectPhases,
  unknown,
  ICreateProductionProjectPhase,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateProductionProjectPhase) =>
      productionService.createProductionProjectPhases(data),
    onSuccess: (data) => {
      queryClient.setQueriesData(
        { queryKey: ["prd-project-phase", data.prdProjectId] },
        (oldData: IProductionProjectPhases[] | undefined) => {
          if (!oldData) return [data];
          return [...oldData, data];
        },
      );
    },
  });
}
export function useDeleteProductionProjectPhases(): UseMutationResult<
  number,
  unknown,
  number,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => productionService.deleteProductionProjectPhases(data),
    onSuccess: (data, variables) => {
      queryClient.setQueriesData(
        { queryKey: ["prd-project-phase", data] },
        (oldData: IProductionProjectPhases[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter((item) => item.id !== variables);
        },
      );
    },
  });
}
export function useDeleteProductionProjectPhaseMilestone(): UseMutationResult<
  number,
  unknown,
  { id: number; projectId: number },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: number; projectId: number }) =>
      productionService.deleteProductionProjectPhaseMilestone(data.id),
    onSuccess: (data, variables) => {
      queryClient.setQueriesData(
        { queryKey: ["prd-project-phase", variables.projectId] },
        (oldData: IProductionProjectPhases[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((item) => {
            if (item.id === data.id) {
              return {
                ...item,
                prdProjectPhaseMilestones:
                  item.prdProjectPhaseMilestones?.filter(
                    (milestone) => milestone.id !== variables.id,
                  ),
              };
            }
            return item;
          });
        },
      );
    },
  });
}
export function useCreateProductionProjectPhaseMilestone(
  projectId: number,
): UseMutationResult<
  IProductionProjectPhaseMilestone,
  unknown,
  ICreateProductionProjectPhaseMilestone
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateProductionProjectPhaseMilestone) =>
      productionService.createProductionProjectPhaseMilestone(data),
    onSuccess: (data) => {
      queryClient.setQueriesData(
        { queryKey: ["prd-project-phase", projectId] },
        (oldData: IProductionProjectPhases[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((item) => {
            if (item.id === data.prdProjectPhaseId) {
              return {
                ...item,
                prdProjectPhaseMilestones: [
                  ...(item.prdProjectPhaseMilestones || []),
                  data,
                ],
              };
            }
            return item;
          });
        },
      );
    },
  });
}
export function useUploadDocumentProduction(): UseMutationResult<
  IProductionProjectDocuments,
  unknown,
  ICreateProductionProjectDocuments,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateProductionProjectDocuments) =>
      productionService.uploadProductionDocument(data),
    onSuccess: (data) => {
      queryClient.setQueriesData(
        { queryKey: ["prd-document-project", data.prdProjectId] },
        (oldData: IProductionProjectDocuments[] | undefined) => {
          if (!oldData) return [data];
          return [...oldData, data];
        },
      );
    },
  });
}
export function useGetProductionDocumentByProjectId(
  projectId: number,
): UseQueryResult<IProductionProjectDocuments[]> {
  return useQuery({
    queryKey: ["prd-document-project", projectId],
    queryFn: () => productionService.getDocumentByProjectId(projectId),
  });
}
export function useGetHistoryDownloadDocument(
  documentId: number,
): UseQueryResult<IProductionProjectDocumentDownloadHistory[]> {
  return useQuery({
    queryKey: ["prd-history-download-document", documentId],
    queryFn: () =>
      productionService.getProductionDownloadHistoryDocument(documentId),
  });
}
export function useDeleteProductionDocumentProject(): UseMutationResult<
  number,
  unknown,
  number,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => productionService.deleteDocumentProject(id),
    onSuccess: (data, variables) => {
      queryClient.setQueriesData(
        { queryKey: ["prd-document-project", data] },
        (oldData: IProductionProjectDocuments[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter((item) => item.id !== variables);
        },
      );
    },
  });
}
